import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Stepper, Button, Input, Select, Radio, Textarea, FileUpload, Switch, Alert, Badge, Card } from '@dreamtree-org/twreact-ui';
import { CheckCircle2, Circle, ShieldAlert } from 'lucide-react';
import { PageHeader, SectionCard, KeyValue, QueryState } from '@/components';
import { useMutation, useQuery } from '@/lib/useQuery';
import { useProductTypes, useStoreCustomFields, modelAttributes, specLine, serialLabel, defaultSpecs, attributesFromForm } from '@/lib/productTypes';
import SpecFields from '../components/SpecFields';
import { errorText } from '@/lib/format';
import { useCan } from '@rbac/useCan';
import { useCrudList, useCrudWrite } from '../hooks';
import ListingPreviewCard from '../components/ListingPreviewCard';
import {
  WIZARD_STEPS, BATTERY_METHODS, initialWizardForm, validateStep, inspectionBlockers, buildPayloads, checklistFor, emptyInspection,
  canStockByQty, isLotForm, wizardReadiness, MIN_UNIT_PHOTOS, PHOTO_SHOTS, photosHint,
} from '../lib/wizard';
import { rupeesToPaise } from '../lib/money';
import { maskSerial } from '../lib/serial';
import { uploadFile } from '@/lib/uploads';
import { UNIT_FILE_KINDS, MAX_ALT, altFor } from '../lib/photos';
import { modelName, suggestTitle } from '../lib/catalog';
import { conditionOptions, gradeOptions, gradeRequired } from '@/lib/conditions';
import BrandSelect from '../components/BrandSelect';
import NoProductTypes, { noTypesChosen } from '../components/NoProductTypes';

/**
 * SELL-03 — unit intake: Basic info → Specifications → Condition & inspection →
 * Photos → Publish. Creates REAL rows (ProductModel when new, InventoryUnit
 * with the plaintext serial the backend hashes, the inspection report, photos
 * uploaded against the unit, optional Listing). The server enforces the publish
 * gate (catalog:publish + the readiness rules: inspection, safety, at least three
 * approved photos of the exact unit — MERCH-54) whatever the client does. A
 * listing to publish is saved as a draft first, then published only when the
 * server's readiness check (Listing readiness action) says it is ready.
 * Each step is remembered, so a retry after a failure never duplicates rows.
 */
export default function UnitWizard({ store }) {
  const nav = useNavigate();
  const can = useCan();
  const registry = useProductTypes();
  // A type with the store's own fields (ADR 0009 §4): its spec step shows them after the platform's.
  const customFields = useStoreCustomFields();
  const typeFor = (key) => customFields.withFields(registry.byKey[key] || null);
  const storeTypes = useQuery('storeProductTypes', {}, { select: (r) => (r && r.data) || [] });
  const [f, setF] = useState(initialWizardForm);
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [created, setCreated] = useState({}); // { model, unit, inspection, listing }
  const [submitError, setSubmitError] = useState('');
  const [busy, setBusy] = useState(false);
  const write = useCrudWrite();
  const saveInspection = useMutation('merchantInspectionSave');
  const readinessOf = useMutation('listingReadiness');

  const models = useCrudList('ProductModel', { orderBy: { column: 'brand', direction: 'asc' }, limit: 500 });
  const categories = useCrudList('Category', { limit: 500 });
  const selectedModel = useMemo(
    () => (f.modelMode === 'existing' ? (models.data || []).find((m) => String(m.id) === String(f.product_model_id)) : f.brand ? { ...f, ...attributesFromForm(typeFor(f.product_type), f.specs) } : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- typeFor reads the registry and the store's fields
    [f, models.data, registry.byKey, customFields.types],
  );
  // The unit's product type (D32): its model's, or the one picked for a new model; it decides the
  // spec fields, the serial rule (IMEI for phones/tablets), the battery step and the checklist.
  // No default type (ADR 0009 §1): a model without a known type has no type here, and the server refuses it.
  const typeKey = f.modelMode === 'existing' ? (selectedModel && selectedModel.product_type) || null : f.product_type;
  const type = typeFor(typeKey);
  const items = checklistFor(type, registry.checklists);
  const itemKeys = items.map((i) => i.key).join();
  const ctx = { canPublish: can('catalog', 'publish'), type, items, conditions: registry.conditions };
  // Types sold by quantity (D60) default to a stock lot: a quantity on hand, no serial, no inspection (D61).
  const lot = isLotForm(f, type);
  const hasBattery = (!type || type.battery) && !lot;
  const serialText = serialLabel(type);
  useEffect(() => {
    setF((prev) => ({ ...prev, inspection: { ...emptyInspection(items), ...Object.fromEntries(Object.entries(prev.inspection).filter(([k]) => itemKeys.split(',').includes(k))) } }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset when the checklist changes
  }, [itemKeys]);
  const enabledTypes = (storeTypes.data || []).filter((t) => t.enabled);
  // A new model starts on the store's first type (registry order), with that type's default specs.
  const firstType = (enabledTypes[0] && typeFor(enabledTypes[0].key)) || null;
  useEffect(() => {
    if (firstType) setF((prev) => (prev.product_type ? prev : { ...prev, product_type: firstType.key, specs: defaultSpecs(firstType) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- typeFor gives a fresh object each render; the key is what changes
  }, [firstType && firstType.key]);

  // Photo previews (object URLs) — revoked when the selection changes.
  const previews = useMemo(() => f.photos.map((file) => URL.createObjectURL(file)), [f.photos]);
  useEffect(() => () => previews.forEach((u) => URL.revokeObjectURL(u)), [previews]);

  const set = (patch) => setF((prev) => ({ ...prev, ...patch }));
  const field = (name) => ({ value: f[name], onChange: (e) => set({ [name]: e.target.value }), error: errors[name] });
  const setInspection = (key, patch) => set({ inspection: { ...f.inspection, [key]: { ...f.inspection[key], ...patch } } });

  const stepKey = WIZARD_STEPS[step].key;
  const next = () => {
    const e = validateStep(stepKey, f, ctx);
    setErrors(e);
    if (Object.keys(e).length) return;
    if (stepKey === 'photos' && !f.title) set({ title: suggestTitle(selectedModel) });
    setStep((s) => Math.min(s + 1, WIZARD_STEPS.length - 1));
  };
  const back = () => {
    setErrors({});
    setStep((s) => Math.max(0, s - 1));
  };

  const submit = async () => {
    // Re-validate every step: the publish gate cannot be bypassed by jumping around.
    for (let i = 0; i < WIZARD_STEPS.length; i += 1) {
      const e = validateStep(WIZARD_STEPS[i].key, f, ctx);
      if (Object.keys(e).length) {
        setErrors(e);
        setStep(i);
        return;
      }
    }
    setBusy(true);
    setSubmitError('');
    const p = buildPayloads(f, ctx);
    const done = { ...created };
    try {
      if (p.model && !done.model) done.model = await write.create('ProductModel', p.model);
      const modelId = p.model ? done.model && done.model.id : p.unit.product_model_id;
      if (!modelId) throw new Error('The model was not created');
      if (!done.unit) done.unit = await write.create('InventoryUnit', { ...p.unit, product_model_id: modelId });
      if (!done.unit || !done.unit.id) throw new Error('The unit was not created');
      if (!done.inspection && lot) done.inspection = { skipped: true };
      if (!done.inspection) {
        const res = await saveInspection.run({
          unit_id: done.unit.id,
          technician: f.technician,
          results: f.inspection,
          battery: hasBattery ? { method: f.battery_method || null, health_pct: f.battery_health_pct === '' ? null : Number(f.battery_health_pct) } : {},
          accessories: f.accessories,
        });
        done.inspection = res.data;
      }
      // Photos of this exact unit; each uploaded file is remembered so a retry skips it.
      done.photos = done.photos || [];
      for (const [i, file] of f.photos.entries()) {
        if (done.photos[i]) continue;
        done.photos[i] = await uploadFile(file, { ownerType: 'inventory_unit', ownerId: done.unit.id, kind: 'photo' });
      }
      // Illustrative images (MERCH-77): kept apart from the photos, never counted toward publishing.
      done.illustrative = done.illustrative || [];
      for (const [i, file] of f.illustrative.entries()) {
        if (done.illustrative[i]) continue;
        done.illustrative[i] = await uploadFile(file, { ownerType: 'inventory_unit', ownerId: done.unit.id, kind: 'illustrative', alt: altFor(f.illustrativeAlt, i, f.illustrative.length) });
      }
      const publish = p.listing && p.listing.status === 'published';
      if (p.listing && !done.listing) done.listing = await write.create('Listing', { ...p.listing, status: publish ? 'draft' : p.listing.status, unit_id: done.unit.id });
      if (publish && done.listing && done.listing.status === 'draft') {
        // The server's checklist decides (uploads may be held back); a not-ready listing stays a draft.
        const r = await readinessOf.run({ id: done.listing.id });
        done.readiness = r && r.data;
        if (done.readiness && done.readiness.ready) done.listing = await write.update('Listing', done.listing.id, { status: 'published' });
      }
      setCreated(done);
    } catch (ex) {
      setCreated(done);
      setSubmitError(errorText(ex));
    } finally {
      setBusy(false);
    }
  };

  const finished = created.unit && created.inspection && (!f.createListing || created.listing);
  if (finished) {
    return (
      <div className="space-y-4 max-w-2xl">
        <PageHeader title={created.listing ? 'Listing added' : 'Added to stock'} subtitle={`${modelName(selectedModel)} · ${created.unit.internal_sku}`} />
        <Alert variant="success" title="Saved">
          {lot
            ? `${f.qty} × ${created.unit.internal_sku} ${Number(f.qty) === 1 ? 'is' : 'are'} in stock`
            : `Unit ${created.unit.internal_sku} (serial ${created.unit.serial_masked || maskSerial(f.serial)}) is in stock`}
          {created.listing ? ` and its listing is ${created.listing.status === 'pending_review' ? 'waiting for platform review' : created.listing.status}` : ''}.
        </Alert>
        {created.readiness && !created.readiness.ready ? (
          <Alert variant="warning" title="Saved as a draft: not ready to publish yet">
            <ReadinessList items={created.readiness.missing.map((m) => ({ ...m, ok: false }))} />
          </Alert>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => nav(`/products?tab=${created.listing ? 'listed' : 'stock'}`)}>View in Products</Button>
          {created.listing ? <Button variant="outline" onClick={() => nav(`/listings/${created.listing.id}`)}>Open listing</Button> : null}
          <Button variant="ghost" onClick={() => { setF(initialWizardForm()); setCreated({}); setStep(0); }}>Add another</Button>
        </div>
      </div>
    );
  }

  const header = <PageHeader title="Add listing" subtitle="Any device you sell: pick its type and model, record the unit, inspect it and publish it." back={{ label: 'Products', href: '/products' }} />;
  // No product types chosen yet: nothing can be added, so no form (as on Import).
  if (noTypesChosen(storeTypes.data)) return <div className="space-y-4">{header}<NoProductTypes /></div>;

  const blockers = lot ? [] : inspectionBlockers(f.inspection, items);
  const steps = WIZARD_STEPS.map((s, i) => ({ title: s.title, description: s.description, status: i < step ? 'completed' : i === step ? 'current' : 'upcoming' }));

  return (
    <div className="space-y-4">
      {header}

      <Card className="p-4 overflow-x-auto">
        <div className="hidden sm:block min-w-[36rem]">
          <Stepper steps={steps} currentStep={step} orientation="horizontal" onStepClick={(_, i) => i < step && setStep(i)} />
        </div>
        <div className="sm:hidden text-sm">
          <span className="font-semibold">Step {step + 1} of {WIZARD_STEPS.length}:</span> {WIZARD_STEPS[step].title}
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-4">
          {stepKey === 'basic' ? (
            <SectionCard title="Product type & model">
              <div className="flex flex-wrap gap-6">
                <Radio name="modelMode" value="existing" checked={f.modelMode === 'existing'} onChange={() => set({ modelMode: 'existing' })} label="Pick an existing model" />
                <Radio name="modelMode" value="new" checked={f.modelMode === 'new'} onChange={() => set({ modelMode: 'new', product_model_id: '' })} label="Create a new model" />
              </div>
              {f.modelMode === 'existing' ? (
                <QueryState query={models}>
                  {(rows) => (
                    <Select
                      label="Model"
                      required
                      searchable
                      closeOnSelect
                      placeholder="Search brand or model…"
                      options={(rows || []).map((m) => {
                        const t = registry.byKey[m.product_type];
                        return { value: String(m.id), label: [modelName(m), t && t.label, specLine(t, modelAttributes(m))].filter(Boolean).join(' — ') };
                      })}
                      value={f.product_model_id}
                      onChange={(v) => set({ product_model_id: v })}
                      error={errors.product_model_id}
                      helperText={(rows || []).length ? undefined : 'No models yet — create a new one.'}
                    />
                  )}
                </QueryState>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2">
                  <Select
                    label="Product type"
                    required
                    options={enabledTypes.map((t) => ({ value: t.key, label: t.label }))}
                    value={f.product_type}
                    onChange={(v) => v && set({ product_type: v, specs: defaultSpecs(typeFor(v)), category_id: '' })}
                    error={errors.product_type}
                    helperText="Add more types under Settings → What you sell."
                    closeOnSelect
                  />
                  <BrandSelect required typeKey={f.product_type} value={f.brand_id} onChange={(id, name) => set({ brand_id: id, brand: name })} error={errors.brand_id} />
                  <Input label="Series" placeholder="Latitude" {...field('series')} />
                  <Input label="Model" required placeholder="7420" {...field('model_name')} />
                  <Select
                    label="Category"
                    placeholder="Optional"
                    options={(categories.data || []).filter((c) => c.product_type === f.product_type).map((c) => ({ value: String(c.id), label: c.name }))}
                    value={f.category_id}
                    onChange={(v) => set({ category_id: v })}
                    allowClear
                    closeOnSelect
                  />
                </div>
              )}
            </SectionCard>
          ) : null}

          {stepKey === 'specs' ? (
            <>
              {f.modelMode === 'new' && type ? (
                <SectionCard title={`${type.label} specifications`}>
                  <SpecFields
                    type={type}
                    value={f.specs}
                    onChange={(patch) => set({ specs: { ...f.specs, ...patch } })}
                    errors={Object.fromEntries(Object.entries(errors).filter(([k]) => k.startsWith('specs.')).map(([k, v]) => [k.slice(6), v]))}
                  />
                </SectionCard>
              ) : f.modelMode === 'new' ? (
                <Alert variant="warning">Pick a product type for the new model in the first step.</Alert>
              ) : (
                <SectionCard title="Model specifications">
                  <KeyValue
                    columns={2}
                    items={[
                      { label: 'Model', value: modelName(selectedModel) },
                      { label: 'Type', value: type && type.label },
                      { label: 'Specs', value: selectedModel && specLine(type, modelAttributes(selectedModel)) },
                    ]}
                  />
                </SectionCard>
              )}
              <SectionCard title={lot ? 'This stock' : 'This unit'}>
                {canStockByQty(type) ? (
                  <Switch
                    checked={lot}
                    onChange={(on) => set({ stockMode: on ? 'quantity' : 'unit' })}
                    rightLabel="Sell by quantity (identical items under one SKU, no serial numbers)"
                  />
                ) : null}
                <div className="grid gap-3 sm:grid-cols-2">
                  <Input label="Internal SKU" required placeholder={lot ? 'ANK-65W-USBC' : 'DEL-7420-101'} {...field('internal_sku')} helperText={lot ? 'Staff scan this SKU when they pick an order' : undefined} />
                  {lot ? (
                    <Input label="Quantity in stock" type="number" required {...field('qty')} />
                  ) : (
                    <Input
                      label={serialText.label}
                      required
                      placeholder="Scan or type"
                      {...field('serial')}
                      helperText={f.serial ? `Stored hashed; shown as ${maskSerial(f.serial)}` : [serialText.hint, 'Stored hashed, never shown in full'].filter(Boolean).join(' ')}
                    />
                  )}
                  <Select label="Condition" options={conditionOptions(registry.conditions, type)} value={f.condition} onChange={(v) => set({ condition: v })} error={errors.condition} closeOnSelect />
                  {gradeRequired(registry.conditions, f.condition) ? (
                    <Select label="Grade" options={gradeOptions()} value={f.grade} onChange={(v) => set({ grade: v })} error={errors.grade} closeOnSelect />
                  ) : null}
                  {hasBattery ? (
                    <>
                      <Input label="Battery health (%)" type="number" {...field('battery_health_pct')} />
                      <Select
                        label="Battery measured by"
                        placeholder="Method"
                        options={BATTERY_METHODS}
                        value={f.battery_method}
                        onChange={(v) => set({ battery_method: v })}
                        error={errors.battery_method}
                        closeOnSelect
                      />
                    </>
                  ) : null}
                  <Input label="Acquisition cost (₹)" placeholder="22500" {...field('acquisition_cost')} />
                  <Input label="Accessories included" placeholder="Original 65W charger" {...field('accessories')} />
                </div>
                <Textarea label="Notes" rows={2} value={f.notes} onChange={(e) => set({ notes: e.target.value })} />
              </SectionCard>
            </>
          ) : null}

          {stepKey === 'inspection' && lot ? (
            <SectionCard title="Condition & inspection checklist">
              <Alert variant="info">Items sold by quantity are new or boxed stock, so no per-item inspection is needed. Continue to photos.</Alert>
            </SectionCard>
          ) : null}
          {stepKey === 'inspection' && !lot ? (
            <SectionCard title="Condition & inspection checklist">
              <p className="text-sm opacity-70">Mark every check. Items tagged <Badge variant="warning" size="sm">Safety</Badge> must pass before the unit can be published.</p>
              <ul className="divide-y divide-[var(--app-border)]">
                {items.map((item) => {
                  const r = f.inspection[item.key] || { result: '', note: '' };
                  const err = errors[`inspection.${item.key}`];
                  return (
                    <li key={item.key} className="py-3 space-y-2">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="font-medium flex items-center gap-2">
                          {item.label}
                          {item.safety ? <Badge variant="warning" size="sm">Safety</Badge> : null}
                        </span>
                        <div className="flex gap-4" role="radiogroup" aria-label={item.label}>
                          <Radio name={`ins-${item.key}`} value="pass" checked={r.result === 'pass'} onChange={() => setInspection(item.key, { result: 'pass' })} label="Pass" />
                          <Radio name={`ins-${item.key}`} value="fail" checked={r.result === 'fail'} onChange={() => setInspection(item.key, { result: 'fail' })} label="Fail" />
                        </div>
                      </div>
                      <Input placeholder={r.result === 'fail' ? 'What failed? (required)' : 'Note (optional)'} value={r.note} onChange={(e) => setInspection(item.key, { note: e.target.value })} error={err} aria-label={`${item.label} note`} />
                    </li>
                  );
                })}
              </ul>
              <Input label="Technician" required placeholder="Who ran the checks" {...field('technician')} />
            </SectionCard>
          ) : null}

          {stepKey === 'photos' ? (
            <>
            <SectionCard title={UNIT_FILE_KINDS.photo.title()}>
              <p className="text-sm opacity-70">
                {lot
                  ? 'Optional for stock sold by quantity: a photo of the product or its box.'
                  : `Take genuine photos of this exact unit, not stock or catalogue images; at least ${MIN_UNIT_PHOTOS} to publish. Shoppers buy this one, so they see it as it is:`}
              </p>
              {lot ? null : (
                <ul className="list-disc pl-5 text-sm opacity-80">
                  {PHOTO_SHOTS.map((s) => <li key={s}>{s}</li>)}
                </ul>
              )}
              <FileUpload accept="image/*" multiple maxSize={8 * 1024 * 1024} files={f.photos} onFileSelect={(files) => set({ photos: files })} onFileRemove={(i) => set({ photos: f.photos.filter((_, k) => k !== i) })} />
              {errors.photos ? <Alert variant="error">{errors.photos}</Alert> : null}
              {!lot && photosHint(f.photos.length) ? <Alert variant="info">{photosHint(f.photos.length)}</Alert> : null}
              {previews.length ? (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {previews.map((u, i) => <img key={u} src={u} alt={`Unit photo ${i + 1}`} className="aspect-square w-full object-cover rounded-md border border-[var(--app-border)]" />)}
                </div>
              ) : null}
            </SectionCard>
            <SectionCard title={UNIT_FILE_KINDS.illustrative.title()}>
              <p className="text-sm opacity-70">
                Optional. Generated, AI-made or stock images go here, never above. They do not count toward publishing, and shoppers see them only on the product page, after the real photos, labelled &quot;Illustrative image — not this unit&quot;.
              </p>
              <FileUpload accept="image/jpeg,image/png,image/webp" multiple maxSize={8 * 1024 * 1024} files={f.illustrative} onFileSelect={(files) => set({ illustrative: files })} onFileRemove={(i) => set({ illustrative: f.illustrative.filter((_, k) => k !== i) })} />
              {f.illustrative.length ? (
                <Input label="What these images show (alt text)" required maxLength={MAX_ALT} value={f.illustrativeAlt} onChange={(e) => set({ illustrativeAlt: e.target.value })} />
              ) : null}
              {errors.illustrative ? <Alert variant="error">{errors.illustrative}</Alert> : null}
            </SectionCard>
            </>
          ) : null}

          {stepKey === 'publish' ? (
            <SectionCard title="Publish">
              <Switch checked={f.createListing} onChange={(v) => set({ createListing: v })} rightLabel="Create a storefront listing for this unit" />
              {f.createListing ? (
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="sm:col-span-2"><Input label="Listing title" required {...field('title')} /></div>
                  <Input label="Selling price (₹)" required placeholder="28999" {...field('price')} />
                  <Input label="MRP (₹)" placeholder="38999" {...field('mrp')} />
                  <Input label="Warranty (months)" type="number" {...field('warranty_months')} />
                  <div className="space-y-2">
                    <span className="text-sm font-medium">Status</span>
                    <div className="flex flex-wrap gap-4">
                      <Radio name="listingStatus" value="draft" checked={f.listingStatus === 'draft'} onChange={() => set({ listingStatus: 'draft' })} label="Draft" />
                      <Radio
                        name="listingStatus"
                        value="published"
                        checked={f.listingStatus === 'published'}
                        onChange={() => set({ listingStatus: 'published' })}
                        label="Published"
                        disabled={!ctx.canPublish || blockers.length > 0}
                      />
                    </div>
                  </div>
                </div>
              ) : null}
              {f.createListing && f.listingStatus === 'published' ? (
                <div className="space-y-1">
                  <span className="text-sm font-medium">Ready to publish?</span>
                  <ReadinessList items={wizardReadiness(f, ctx)} />
                  <p className="text-xs opacity-70">The listing is saved as a draft first; it is published once the store confirms every item.</p>
                </div>
              ) : null}
              {f.createListing && blockers.length ? (
                <Alert variant="warning" title="Publishing is blocked">
                  <span className="flex items-start gap-2"><ShieldAlert size={16} className="mt-0.5 shrink-0" />Safety checks not passed: {blockers.join(', ')}. Save as draft and re-inspect.</span>
                </Alert>
              ) : null}
              {f.createListing && !ctx.canPublish ? <Alert variant="info">You can save drafts; publishing needs the catalog publish permission.</Alert> : null}
              {errors.listingStatus ? <Alert variant="error">{errors.listingStatus}</Alert> : null}
            </SectionCard>
          ) : null}

          {submitError ? (
            <Alert variant="error" title="Not finished">
              {submitError}
              {created.unit ? ` The unit ${created.unit.internal_sku} was saved; retrying continues from where it stopped.` : ''}
            </Alert>
          ) : null}

          <div className="flex flex-wrap justify-between gap-2">
            <Button variant="outline" onClick={back} disabled={step === 0 || busy}>Back</Button>
            {step < WIZARD_STEPS.length - 1 ? (
              <Button onClick={next}>Next</Button>
            ) : (
              <Button onClick={submit} loading={busy} leftIcon={<CheckCircle2 size={16} />}>
                {f.createListing ? (f.listingStatus === 'published' ? 'Save & publish' : 'Save unit & draft listing') : 'Save unit'}
              </Button>
            )}
          </div>
        </div>

        <aside className="space-y-4">
          <SectionCard title="Summary">
            <KeyValue
              items={[
                { label: 'Model', value: selectedModel ? modelName(selectedModel) : '' },
                { label: 'SKU', value: f.internal_sku },
                lot ? { label: 'Quantity', value: f.qty } : { label: 'Serial', value: f.serial ? <span className="font-mono">{maskSerial(f.serial)}</span> : '' },
                { label: 'Grade', value: f.grade },
                { label: 'Battery', value: f.battery_health_pct ? `${f.battery_health_pct}%` : '' },
                { label: 'Checks passed', value: lot ? 'Not needed' : `${items.filter((i) => (f.inspection[i.key] || {}).result === 'pass').length}/${items.length}` },
                { label: 'Photos', value: f.photos.length },
                ...(f.illustrative.length ? [{ label: 'Illustrative images', value: f.illustrative.length }] : []),
              ]}
            />
          </SectionCard>
          {stepKey === 'publish' && f.createListing ? (
            <ListingPreviewCard
              title={f.title}
              pricePaise={rupeesToPaise(f.price)}
              mrpPaise={rupeesToPaise(f.mrp)}
              warrantyMonths={Number(f.warranty_months) || 0}
              unit={{ grade: f.grade, condition: f.condition, battery_health_pct: f.battery_health_pct === '' ? null : Number(f.battery_health_pct) }}
              model={selectedModel}
              photoUrl={previews[0]}
              storeName={store && store.name}
            />
          ) : null}
        </aside>
      </div>
    </div>
  );
}

/** A readiness checklist: [{ key, label, ok, detail }]. */
function ReadinessList({ items }) {
  return (
    <ul className="space-y-1 text-sm" aria-label="Publish checklist">
      {items.map((i) => (
        <li key={i.key} className="flex items-start gap-2">
          {i.ok ? <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-success-600" aria-label="Done" /> : <Circle size={16} className="mt-0.5 shrink-0 opacity-60" aria-label="Missing" />}
          <span>
            {i.label}
            {i.detail ? <span className="block text-xs opacity-70">{i.detail}</span> : null}
          </span>
        </li>
      ))}
    </ul>
  );
}
